import { createSelector } from 'reselect';
import { v4 as uuid } from 'uuid';

import type {
  Transcription,
  TranscriptionSeries,
  SpeakerTranscriptSegment,
  TranscriptionViewable,
  TranscriptionViewableWords,
} from '@common-modules/mediaDetails/models';
import { getSeriesSpeakerId } from '@common-modules/mediaDetails/models';
import { selectStore } from './view';
import {
  DEFAULT_EMPTY_TRANSCRIPTION,
  PUNCTUATION_REGEX,
  TRANSCRIPTION_ELLIPSIS_MIN_DURATION_MS,
} from '@helpers/constants';

/**
 * The text a gap between words carries.
 *
 * DO NOT retier this to suit the panel. This string is the `words` field of
 * every spacer, and getRedactedTranscript emits it verbatim into the
 * redacted-transcript asset that saveRedactedTranscriptData uploads as
 * text/plain — so a tier change here silently rewrites a delivered artifact
 * and the `transcriptLength` recorded against it. An earlier pass on this
 * branch dropped the 10s tier for a display reason and did exactly that.
 *
 * The panel's own two-tier treatment is a separate decision made at the panel:
 * anything past TRANSCRIPTION_GAP_ROW_MIN_DURATION_MS is drawn as a labelled
 * rule rather than as this text — see Transcription/Display/transcriptGap.ts —
 * which needs nothing from these tiers.
 *
 * The '-' is never seen. Every consumer filters spacers below the ellipsis
 * threshold; it exists so a spacer always has text.
 */
function formatSpacer(startTimeMs: number, stopTimeMs: number) {
  const dur = stopTimeMs - startTimeMs;
  if (dur > 30000) {
    return '.........';
  }
  if (dur > 10000) {
    return '......';
  }
  if (dur >= TRANSCRIPTION_ELLIPSIS_MIN_DURATION_MS) {
    return '...';
  }
  return '-';
}

const speakerLabelForId = (speakerId: string, labelIndex: number) => {
  if (!speakerId || speakerId === '?') {
    return `S${labelIndex + 1}`;
  }
  if (/^\d+$/.test(speakerId)) {
    return `S${speakerId}`;
  }
  if (/^[A-Za-z]$/.test(speakerId)) {
    return `S${speakerId.toUpperCase().charCodeAt(0) - 64}`;
  }
  return `S${labelIndex + 1}`;
};

const seriesToViewableWord = (
  series: TranscriptionSeries,
  index: number
): TranscriptionViewableWords => {
  const wordList = series.words ?? [];
  const wordsLen = wordList.length || 1;
  return {
    id: `transcription_${index}`,
    startTimeMs: series.startTimeMs,
    stopTimeMs: series.stopTimeMs,
    ...wordList.reduce(
      ({ words, confidence }, w) => ({
        words: `${words} ${w.word}`.trim(),
        confidence: confidence + w.confidence / wordsLen,
        isSpacer: false,
      }),
      { words: '', confidence: 0, isSpacer: false }
    ),
  };
};

const isSpeakerTimelineSeries = (series: TranscriptionSeries) =>
  !!getSeriesSpeakerId(series) ||
  series.object?.type === 'speaker' ||
  (series.words?.length ?? 0) > 0;

const toTranscriptionViewableShell = (
  transcription: Transcription,
  words: ReadonlyArray<TranscriptionViewableWords>
): TranscriptionViewable => ({
  id: uuid(),
  engineId: transcriptionEngineId(transcription),
  startOffsetMs: 0,
  stopOffsetMs: transcription.stopOffsetMs,
  transcription: words,
});

/** the asset records it on itself, older ones only inside jsondata */
const transcriptionEngineId = (transcription: Transcription) =>
  transcription.engineId ?? transcription.jsondata?.sourceEngineId;

/** Requires `speakerRanges` sorted by `startTimeMs`. */
const resolveSpeakerIdAtTime = (
  timeMs: number,
  speakerRanges: ReadonlyArray<TranscriptionSeries>
): string => {
  if (!speakerRanges.length) {
    return 'unknown';
  }

  let lo = 0;
  let hi = speakerRanges.length - 1;
  let lastStartAtOrBefore = -1;

  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const range = speakerRanges[mid]!;
    if (range.startTimeMs <= timeMs) {
      lastStartAtOrBefore = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }

  if (lastStartAtOrBefore >= 0) {
    const containing = speakerRanges[lastStartAtOrBefore]!;
    if (timeMs <= containing.stopTimeMs) {
      return getSeriesSpeakerId(containing) ?? 'unknown';
    }
  }

  const nearestCandidates = new Set<number>();
  if (lastStartAtOrBefore >= 0) {
    nearestCandidates.add(lastStartAtOrBefore);
  }
  if (lastStartAtOrBefore + 1 < speakerRanges.length) {
    nearestCandidates.add(lastStartAtOrBefore + 1);
  }
  if (lastStartAtOrBefore - 1 >= 0) {
    nearestCandidates.add(lastStartAtOrBefore - 1);
  }

  let bestSpeakerId = 'unknown';
  let bestDistance = Infinity;
  for (const idx of nearestCandidates) {
    const range = speakerRanges[idx]!;
    // Distance to the nearest EDGE, not to the centre. By centre, a two-second
    // range that ended 10ms ago loses to a 100ms range that starts in a
    // second, because the short range's midpoint is closer — so a word in the
    // gap was attributed to whoever spoke briefly nearby rather than to
    // whoever was speaking either side of it.
    const distance = Math.max(
      range.startTimeMs - timeMs,
      timeMs - range.stopTimeMs,
      0
    );
    if (distance < bestDistance) {
      bestDistance = distance;
      bestSpeakerId = getSeriesSpeakerId(range) ?? 'unknown';
    }
  }
  return bestSpeakerId;
};

const labelForSpeakerId = (
  speakerId: string,
  speakerIdToLabel: Map<string, string>,
  usedLabels: Set<string>
) => {
  if (!speakerIdToLabel.has(speakerId)) {
    let label = speakerLabelForId(speakerId, speakerIdToLabel.size);
    let fallbackIndex = speakerIdToLabel.size;
    while (usedLabels.has(label)) {
      fallbackIndex += 1;
      label = `S${fallbackIndex + 1}`;
    }
    usedLabels.add(label);
    speakerIdToLabel.set(speakerId, label);
  }
  return speakerIdToLabel.get(speakerId)!;
};

interface MutableSpeakerSegment {
  speakerId: string;
  speakerLabel: string;
  startTimeMs: number;
  stopTimeMs: number;
  transcription: TranscriptionViewableWords[];
}

const buildSpeakerViewFromSeries = (
  seriesList: ReadonlyArray<TranscriptionSeries>,
  getSpeakerId: (series: TranscriptionSeries) => string,
  options?: {
    attachGapSpacersToSegment?: boolean;
    omitEmptySegments?: boolean;
  }
): {
  flatWords: TranscriptionViewableWords[];
  speakerSegments: SpeakerTranscriptSegment[];
} => {
  const speakerIdToLabel = new Map<string, string>();
  const usedSpeakerLabels = new Set<string>();
  const speakerSegments: SpeakerTranscriptSegment[] = [];
  let currentSegment: MutableSpeakerSegment | undefined;
  let wordIndex = 0;
  let lastStopOffsetMs = 0;
  const flatWords: TranscriptionViewableWords[] = [];

  for (const series of seriesList) {
    const speakerId = getSpeakerId(series);
    const speakerLabel = labelForSpeakerId(
      speakerId,
      speakerIdToLabel,
      usedSpeakerLabels
    );

    if (lastStopOffsetMs < series.startTimeMs) {
      const spacer: TranscriptionViewableWords = {
        id: `transcription_${wordIndex++}`,
        startTimeMs: lastStopOffsetMs,
        stopTimeMs: series.startTimeMs,
        words: formatSpacer(lastStopOffsetMs, series.startTimeMs),
        isSpacer: true,
        confidence: 1,
      };
      flatWords.push(spacer);
      if (options?.attachGapSpacersToSegment && currentSegment) {
        currentSegment.transcription.push(spacer);
      }
    }

    const word = seriesToViewableWord(series, wordIndex++);
    flatWords.push(word);
    lastStopOffsetMs = series.stopTimeMs;

    // Punctuation should stay with the preceding word's segment instead of
    // starting a new speaker segment with a leading period.
    const isPunctuation = !word.isSpacer && PUNCTUATION_REGEX.test(word.words);
    const continueCurrentSegment =
      !!currentSegment &&
      (isPunctuation || currentSegment.speakerId === speakerId);

    if (currentSegment && continueCurrentSegment) {
      currentSegment.stopTimeMs = series.stopTimeMs;
      currentSegment.transcription.push(word);
    } else {
      if (currentSegment) {
        speakerSegments.push({ ...currentSegment });
      }
      currentSegment = {
        speakerId,
        speakerLabel,
        startTimeMs: series.startTimeMs,
        stopTimeMs: series.stopTimeMs,
        transcription: [word],
      };
    }
  }

  if (currentSegment) {
    speakerSegments.push({ ...currentSegment });
  }

  const segments = options?.omitEmptySegments
    ? speakerSegments.filter((segment) =>
        segment.transcription.some((w) => !w.isSpacer)
      )
    : speakerSegments;

  return { flatWords, speakerSegments: segments };
};

export const mergeTranscriptionViewable = (
  transcript?: Transcription,
  speakerSeparation?: Transcription
): TranscriptionViewable => {
  const speakerSeries = speakerSeparation?.jsondata?.series;
  if (!speakerSeries?.length) {
    return handleTranscription(transcript);
  }

  const hasWordsOnSpeakerSeries = speakerSeries.some(
    (s) => (s.words?.length ?? 0) > 0
  );
  if (hasWordsOnSpeakerSeries) {
    return handleSpeakerSeparationTranscription(
      speakerSeparation ?? transcript
    );
  }

  const transcriptSeries = (transcript?.jsondata?.series ?? []).filter(
    (series) => (series.words?.length ?? 0) > 0
  );
  if (!transcriptSeries.length) {
    return handleTranscription(transcript);
  }

  const speakerRanges = speakerSeries
    .filter(isSpeakerTimelineSeries)
    .sort((a, b) => a.startTimeMs - b.startTimeMs);

  const { flatWords, speakerSegments } = buildSpeakerViewFromSeries(
    transcriptSeries,
    (series) =>
      resolveSpeakerIdAtTime(
        (series.startTimeMs + series.stopTimeMs) / 2,
        speakerRanges
      ),
    { attachGapSpacersToSegment: true, omitEmptySegments: true }
  );

  if (!transcript) {
    return {
      id: uuid(),
      speakerSeparationAssetIds: speakerSeparation?.assetIds,
      startOffsetMs: 0,
      stopOffsetMs: 0,
      transcription: flatWords,
      speakerSegments: speakerSegments.length ? speakerSegments : undefined,
    };
  }

  return {
    ...toTranscriptionViewableShell(transcript, flatWords),
    speakerSeparationAssetIds: speakerSeparation?.assetIds,
    speakerSegments: speakerSegments.length ? speakerSegments : undefined,
  };
};

export const handleSpeakerSeparationTranscription = (
  transcription?: Transcription
) => {
  const seriesList = (transcription?.jsondata?.series ?? []).filter(
    (series) => (series.words?.length ?? 0) > 0
  );
  if (!seriesList.length) {
    return handleTranscription(transcription);
  }

  const { flatWords, speakerSegments } = buildSpeakerViewFromSeries(
    seriesList,
    (series) => getSeriesSpeakerId(series) ?? 'unknown'
  );

  if (!flatWords.length || !transcription) {
    return handleTranscription(transcription);
  }

  return {
    ...toTranscriptionViewableShell(transcription, flatWords),
    speakerSeparationAssetIds: transcription.assetIds,
    speakerSegments,
  };
};

export const handleTranscription = (transcription?: Transcription) => {
  if (transcription) {
    const newTranscription: TranscriptionViewableWords[] = [];
    let index = 0;
    let lastStopOffsetMs = 0;
    for (const series of transcription.jsondata.series ?? []) {
      if (lastStopOffsetMs < series.startTimeMs) {
        const spacerIndex = index++;
        newTranscription[spacerIndex] = {
          id: `transcription_${spacerIndex}`,
          startTimeMs: lastStopOffsetMs,
          stopTimeMs: series.startTimeMs,
          words: formatSpacer(lastStopOffsetMs, series.startTimeMs),
          isSpacer: true,
          confidence: 1,
        };
      }
      const word = seriesToViewableWord(series, index);
      newTranscription[index++] = word;
      lastStopOffsetMs = series.stopTimeMs;
    }

    const out: TranscriptionViewable = {
      id: uuid(),
      engineId: transcriptionEngineId(transcription),
      startOffsetMs: 0,
      stopOffsetMs: transcription.stopOffsetMs,
      transcription: newTranscription,
    };

    return out;
  }
  return DEFAULT_EMPTY_TRANSCRIPTION;
};

export const selectTranscription = createSelector(selectStore, (s) =>
  handleTranscription(s.transcription)
);

export const selectTranscriptionEngineResults = createSelector(
  selectStore,
  (s): Transcription | undefined => s.transcription
);

export const selectSpeakerSeparationEngineResults = createSelector(
  selectStore,
  (s): Transcription | undefined => s.speakerSeparation
);

export const selectAudioRedactions = createSelector(
  selectStore,
  (s) => s.audioRedactionEdits
);

export const selectVoiceMaskEdits = createSelector(
  selectStore,
  (s) => s.voiceMaskEdits
);

export const selectAudioEdits = createSelector(selectStore, (s) =>
  s.audioRedactionEdits.concat(s.voiceMaskEdits).sort((a, b) => a[0] - b[0])
);

export const selectAssetCheckpoint = createSelector(
  selectStore,
  (s) => s.assetCheckpoint
);

export const selectOpenedAssetCheckpoint = createSelector(
  selectStore,
  (s) => s.openedAssetCheckpoint
);
